/**
 * DocumentReviewHistoryTab
 *
 * Timeline of document history: audit actions, comments (when/what),
 * and note edits. Used in the History tab of the document review page.
 */

import { Box, Stack, Text } from '@mantine/core';
import { IconMessageCircle, IconHistory, IconCheck, IconPencil } from '@tabler/icons-react';
import { useMemo } from 'react';
import type { AuditEntry, DocumentComment, DocumentHistoryEntry } from '../types';

const auditActionLabels: Record<AuditEntry['action'], string> = {
  reviewed: 'Reviewed',
  commented: 'Commented',
  'requested-edits': 'Requested edits',
  signed: 'Signed',
};

interface DocumentReviewHistoryTabProps {
  auditEntries: AuditEntry[];
  comments: DocumentComment[];
  noteEdits: Array<{
    id: string;
    timestamp: string;
    actorId: string;
    actorName: string;
    description: string;
    summary?: string;
  }>;
}

function getTimestamp(entry: DocumentHistoryEntry): string {
  return entry.timestamp;
}

export function DocumentReviewHistoryTab({
  auditEntries,
  comments,
  noteEdits,
}: DocumentReviewHistoryTabProps): JSX.Element {
  const sortedEntries = useMemo(() => {
    const list: DocumentHistoryEntry[] = [
      ...auditEntries.map((e) => ({
        type: 'audit' as const,
        id: e.id,
        timestamp: e.timestamp,
        action: e.action,
        actorId: e.actorId,
        actorName: e.actorName,
        details: e.details,
      })),
      ...comments.map((c) => ({
        type: 'comment' as const,
        id: c.id,
        timestamp: c.createdAt,
        authorId: c.authorId,
        authorName: c.authorName,
        text: c.text,
        lineStart: c.anchor?.lineStart,
        lineEnd: c.anchor?.lineEnd,
        snippet: c.anchor?.snippet,
        resolvedAt: c.resolvedAt,
      })),
      ...noteEdits.map((e) => ({
        type: 'edit' as const,
        id: e.id,
        timestamp: e.timestamp,
        actorId: e.actorId,
        actorName: e.actorName,
        description: e.description,
        summary: e.summary,
      })),
    ];
    list.sort((a, b) => new Date(getTimestamp(b)).getTime() - new Date(getTimestamp(a)).getTime());
    return list;
  }, [auditEntries, comments, noteEdits]);

  if (sortedEntries.length === 0) {
    return (
      <Box py="lg">
        <Text size="sm" c="gray.6">
          No history recorded yet.
        </Text>
      </Box>
    );
  }

  return (
    <Stack gap={0}>
      <Text size="sm" fw={600} mb="lg" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <IconHistory size={16} />
        Note history
      </Text>
      <Stack gap="xs">
        {sortedEntries.map((entry) => {
          const ts = new Date(getTimestamp(entry)).toLocaleString();
          if (entry.type === 'audit') {
            return (
              <Box
                key={entry.id}
                py="md"
                style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
              >
                <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <Box style={{ flexShrink: 0, marginTop: 2 }}>
                    <IconHistory size={16} color="var(--mantine-color-blue-6)" />
                  </Box>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500}>
                      {entry.actorName} · {auditActionLabels[entry.action]}
                    </Text>
                    <Text size="xs" c="gray.6">
                      {ts}
                    </Text>
                    {entry.details && (
                      <Text size="xs" c="gray.6" mt={4}>
                        {entry.details}
                      </Text>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          }
          if (entry.type === 'comment') {
            return (
              <Box
                key={entry.id}
                py="md"
                style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
              >
                <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <Box style={{ flexShrink: 0, marginTop: 2 }}>
                    <IconMessageCircle size={16} color="var(--mantine-color-violet-6)" />
                  </Box>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500}>
                      {entry.authorName} · Comment
                    </Text>
                    <Text size="xs" c="gray.6">
                      {ts}
                      {entry.lineStart != null && (
                        <> · Line {entry.lineEnd != null && entry.lineEnd !== entry.lineStart ? `${entry.lineStart}–${entry.lineEnd}` : entry.lineStart}</>
                      )}
                    </Text>
                    <Text size="sm" mt={4} style={{ whiteSpace: 'pre-wrap' }}>
                      {entry.text}
                    </Text>
                    {entry.resolvedAt && (
                      <Text size="xs" c="green.7" mt={4} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <IconCheck size={12} /> Resolved {new Date(entry.resolvedAt).toLocaleString()}
                      </Text>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          }
          // edit
          return (
            <Box
              key={entry.id}
              py="md"
              style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
            >
              <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <Box style={{ flexShrink: 0, marginTop: 2 }}>
                  <IconPencil size={16} color="var(--mantine-color-teal-6)" />
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500}>
                    {entry.actorName} · Edit
                  </Text>
                  <Text size="xs" c="gray.6">
                    {ts}
                  </Text>
                  <Text size="sm" mt={4}>
                    {entry.description}
                  </Text>
                  {entry.summary && (
                    <Text size="xs" c="gray.6" mt={2}>
                      {entry.summary}
                    </Text>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}
